import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Search, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

type Pair = { formId: string; formName: string; roleId: string; roleName: string }

type AddDealParticipantsModalProps = Readonly<{
	isOpen: boolean
	onClose: () => void
	organizationId: string
	cycleId: string
}>

type UserRow = {
	id: string
	firstName: string | null
	lastName: string | null
	email: string
	externalId?: string | null
}

type UserListProps = Readonly<{
	isLoading: boolean
	rows: UserRow[]
	selectedIds: Set<string>
	onToggle: (userId: string) => void
}>

function pairKey(p: Pick<Pair, 'formId' | 'roleId'>): string {
	return `${p.formId}|${p.roleId}`
}

function UserList({ isLoading, rows, selectedIds, onToggle }: UserListProps) {
	if (isLoading) return <SpinnerWithBackground />
	if (rows.length === 0) {
		return (
			<Text variant='small' className='text-neutral-500 px-3 py-2'>
				No matching users available to add.
			</Text>
		)
	}
	return (
		<div className='max-h-64 overflow-y-auto rounded-md border border-neutral-200 divide-y divide-neutral-100'>
			{rows.map((u) => (
				<button
					key={u.id}
					type='button'
					onClick={() => onToggle(u.id)}
					className='w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-neutral-50'>
					<input type='checkbox' checked={selectedIds.has(u.id)} readOnly className='h-4 w-4' />
					<div className='flex flex-col'>
						<Text variant='small' weight='semibold'>
							{u.firstName} {u.lastName}
						</Text>
						<Text variant='xsmall' className='text-neutral-500'>
							{u.email}
							{u.externalId ? ` · ${u.externalId}` : ''}
						</Text>
					</div>
				</button>
			))}
		</div>
	)
}

/**
 * Wrike 4457106609 — admin-only entry point for adding individuals to an
 * already-launched Deal Team cycle. The picker is constrained to the cycle's
 * existing (formId, roleId) pairs so the CA can't accidentally pick an
 * unrelated org form / role combination and break the CRM-mapped expectations.
 */
export function AddDealParticipantsModal({ isOpen, onClose, organizationId, cycleId }: AddDealParticipantsModalProps) {
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const [searchInput, setSearchInput] = useState('')
	const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
	const [selectedPairKey, setSelectedPairKey] = useState<string>('')

	const pairs = useQuery({
		...trpc.admin.dealTeam.getCycleFormRolePairs.queryOptions({ organizationId, cycleId }),
		enabled: isOpen,
	})

	// Cap at 200 to keep DOM cost reasonable while covering most org sizes. The
	// search input narrows server-side for anything larger; the "Showing N of M"
	// hint below tells the admin when they need to refine.
	const SEARCH_PER_PAGE = 200
	const possibleUsers = useQuery({
		...trpc.admin.cycleParticipants.users.search.queryOptions({
			organizationId,
			cycleId,
			search: searchInput.trim() || undefined,
			page: 1,
			perPage: SEARCH_PER_PAGE,
			customFieldsFilters: {},
		}),
		enabled: isOpen,
	})

	// Reset modal state every time it opens so a stale selection from a previous
	// session doesn't leak across opens.
	useEffect(() => {
		if (isOpen) {
			setSearchInput('')
			setSelectedUserIds(new Set())
			setSelectedPairKey('')
		}
	}, [isOpen])

	// Auto-select the only option when the cycle has exactly one (form, role) pair.
	// Saves a click in the common single-pair CRM-trigger case.
	useEffect(() => {
		if (pairs.data && !selectedPairKey && pairs.data.pairs.length === 1) {
			setSelectedPairKey(pairKey(pairs.data.pairs[0]))
		}
	}, [pairs.data, selectedPairKey])

	const selectedPair = useMemo(
		() => pairs.data?.pairs.find((p) => pairKey(p) === selectedPairKey),
		[pairs.data, selectedPairKey],
	)

	const addMutation = useMutation({
		...trpc.admin.dealTeam.addCycleParticipants.mutationOptions(),
		onSuccess: (data) => {
			const noun = `participant${data.added === 1 ? '' : 's'}`
			showToast(`Added ${data.added} ${noun} to the deal`)
			// Side-effects (reviewer-group propagation and kickoff email) are best-effort
			// on the backend; if either failed, the participants are still committed but
			// the admin needs a heads-up so they can manually retry the missing piece
			// (or escalate). We surface that with a second toast — the green "added"
			// toast keeps the primary signal, the red follow-up flags the gap.
			if (!data.reviewerGroupsOk || !data.kickoffOk) {
				const issues: string[] = []
				if (!data.reviewerGroupsOk) issues.push('reviewer groups')
				if (!data.kickoffOk) issues.push('kickoff email')
				showToast(`Participants added, but ${issues.join(' and ')} failed. Please retry manually.`, 'error')
			}
			queryClient.invalidateQueries({
				predicate: (q) => {
					const key = q.queryKey
					return (
						Array.isArray(key) && Array.isArray(key[0]) && key[0][0] === 'admin' && key[0][1] === 'dealTeamAggregation'
					)
				},
			})
			onClose()
		},
		onError: (err) => {
			showToast(err.message || 'Failed to add participants', 'error')
		},
	})

	const toggle = (userId: string) => {
		setSelectedUserIds((prev) => {
			const next = new Set(prev)
			if (next.has(userId)) next.delete(userId)
			else next.add(userId)
			return next
		})
	}

	const canSubmit = selectedUserIds.size > 0 && !!selectedPair && !addMutation.isPending

	const handleSubmit = () => {
		if (!selectedPair) return
		addMutation.mutate({
			organizationId,
			cycleId,
			userIds: Array.from(selectedUserIds),
			formId: selectedPair.formId,
			roleId: selectedPair.roleId,
		})
	}

	const userRows = useMemo(() => possibleUsers.data?.data ?? [], [possibleUsers.data])
	const totalAvailable = possibleUsers.data?.meta.total ?? 0
	const isTruncated = totalAvailable > userRows.length

	return (
		<Modal isOpen={isOpen} onClose={onClose} title='Add Participants to Deal'>
			<div className='flex flex-col gap-4 p-2 min-w-[480px]'>
				<Text variant='small' className='text-neutral-600'>
					Pick one or more existing users in the organization. They join the cycle on the form and role you select and
					immediately receive their first-step tasks plus the kickoff email.
				</Text>

				<div className='flex flex-col gap-1'>
					<label htmlFor='add-deal-participants-pair' className='text-xs font-medium text-neutral-600'>
						Form and role
					</label>
					<select
						id='add-deal-participants-pair'
						value={selectedPairKey}
						onChange={(e) => setSelectedPairKey(e.target.value)}
						disabled={pairs.isLoading}
						className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'>
						<option value=''>Select form and role…</option>
						{pairs.data?.pairs.map((p) => (
							<option key={pairKey(p)} value={pairKey(p)}>
								{p.formName} · {p.roleName}
							</option>
						))}
					</select>
				</div>

				<div className='flex flex-col gap-2'>
					<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
						People to add
					</Text>
					<Search value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder='Search users' />
					<UserList
						isLoading={possibleUsers.isLoading}
						rows={userRows}
						selectedIds={selectedUserIds}
						onToggle={toggle}
					/>
					{isTruncated && (
						<Text variant='xsmall' className='text-neutral-500'>
							Showing {userRows.length} of {totalAvailable}. Narrow with the search above to find more.
						</Text>
					)}
					{selectedUserIds.size > 0 && (
						<Text variant='xsmall' className='text-neutral-600'>
							{selectedUserIds.size} selected
						</Text>
					)}
				</div>

				<div className='flex justify-end gap-2 pt-2'>
					<Button variant='secondary' onClick={onClose} disabled={addMutation.isPending}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSubmit} disabled={!canSubmit}>
						{addMutation.isPending ? 'Adding…' : 'Add participants'}
					</Button>
				</div>
			</div>
		</Modal>
	)
}
